<template>
  <v-dialog v-model="model" persistent scrollable>
    <v-card>
      <div class="d-flex justify-end pa-2 pb-0">
        <v-btn
          :icon="mdiClose"
          variant="text"
          color="grey-darken-1"
          size="small"
          :aria-label="$t('component.notFoundModel.closeButton')"
          @click="model = false"
        />
      </div>
      <v-card-text class="px-5 py-6 pt-0">
        <intro-step v-if="step === 'intro'" @yes="onIntroYes" @no="onIntroNo" />

        <brand-step v-else-if="step === 'brand'" :brand="brand" @yes="onBrandYes" @no="onBrandNo" />

        <brand-wrong-step
          v-else-if="step === 'brandWrong'"
          :is-multibrand="isMultibrand"
          :brand="brand"
          @back-to-select-brand="onBackToSelectBrand"
          @talk-to-support="onTalkToSupport"
          @cancel="model = false"
        />

        <category-step
          v-else-if="step === 'category'"
          v-model:selected-category-id="selectedCategoryId"
          :categories="categories"
          :bikes="api.bikes"
          @select-bike="onSelectBike"
          @continue="step = 'final'"
        />

        <final-step v-else :loading="loading" @submit="onFinalSubmit" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import { mdiClose } from "@mdi/js";

  import { useApiClient } from "@/composables/useApiClient";
  import { useUtils } from "@/composables/useUtils";
  import { useApiStore } from "@/stores/api";
  import { useAppStore } from "@/stores/app";
  import { showToast } from "@/utils/toast";

  import BrandStep from "./steps/BrandStep.vue";
  import BrandWrongStep from "./steps/BrandWrongStep.vue";
  import CategoryStep from "./steps/CategoryStep.vue";
  import FinalStep from "./steps/FinalStep.vue";
  import IntroStep from "./steps/IntroStep.vue";

  const { t } = useI18n();
  const model = defineModel({ type: Boolean, default: false });
  const emit = defineEmits<{ reported: []; "select-bike": [bikeId: number] }>();

  const api = useApiStore();
  const app = useAppStore();
  const util = useUtils();
  const apiClient = useApiClient();
  const router = useRouter();

  type Step = "intro" | "brand" | "brandWrong" | "category" | "final";

  const step = ref<Step>("intro");
  const selectedCategoryId = ref<number | null>(null);
  const loading = ref(false);
  const brandId = ref<number | null>(null);

  const brand = computed(() => api.bikes[0]?.brand ?? app.capitalizedBrand);
  const isMultibrand = computed(() => api.isMultibrand);

  const categories = computed(() => {
    const map = new Map<number, { id: number; name: string }>();
    api.bikes.forEach((bike) => {
      if (bike.bikeType) {
        map.set(bike.bikeType.id, bike.bikeType);
      }
    });
    return Array.from(map.values());
  });

  watch(model, (isOpen) => {
    if (!isOpen) {
      return;
    }

    step.value = "intro";
    selectedCategoryId.value = null;
    loading.value = false;

    if (api.insuranceCode) {
      apiClient.getInsuranceBrandContext(api.insuranceCode).then((res) => {
        brandId.value = res.id;
      });
      api.getData();
    }
  });

  function onIntroYes() {
    step.value = "brand";
  }

  function onIntroNo() {
    model.value = false;
  }

  function onBrandYes() {
    step.value = "category";
  }

  function onBrandNo() {
    step.value = "brandWrong";
  }

  function onBackToSelectBrand() {
    model.value = false;
    router.push("/select-brand");
  }

  function onTalkToSupport() {
    const message = isMultibrand.value
      ? t("component.notFoundModel.supportWhatsAppTextMultibrand")
      : t("component.notFoundModel.supportWhatsAppTextOwnBrand", { brand: brand.value });
    util.openWhatsApp(import.meta.env.VITE_APP_BKHR_MAIN_PHONE, message);
    model.value = false;
  }

  function onSelectBike(bikeId: number) {
    model.value = false;
    emit("select-bike", bikeId);
  }

  async function onFinalSubmit(payload: { model: string; value: number }) {
    if (brandId.value == null || selectedCategoryId.value == null) {
      return;
    }

    loading.value = true;
    try {
      await api.reportModelNotFound({
        brandId: brandId.value,
        bikeTypeId: selectedCategoryId.value,
        model: payload.model,
        value: payload.value,
      });

      model.value = false;
      emit("reported");
    } catch (error) {
      console.error(error);
      showToast.warning({
        title: t("component.notFoundModel.reportErrorTitle"),
        description: t("component.notFoundModel.reportErrorDescription"),
      });
    } finally {
      loading.value = false;
    }
  }
</script>

<style scoped></style>
